import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Shield, ShieldCheck, ShieldAlert, RefreshCw, Loader2, Filter, ChevronLeft, ChevronRight,
  CheckCircle2, XCircle, ArrowLeft, Download,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Iter-204 — Admin Compliance Audit (Certification document access log).
 *
 * Lists every fetch through GET /api/certifications/document/{cert_id}
 * with viewer name/role, talent, certification type, success/failure
 * reason, IP, user-agent, timestamp. Supports filtering by talent,
 * viewer, cert type, success state, and date range. Useful for
 * SOC2 / state alcohol-board audits and detecting client-side scraping.
 */

const FAILURE_REASON_META = {
  not_admin_or_owner:    { label: 'Unauthorized',     className: 'bg-rose-50 text-rose-700 border border-rose-200' },
  not_found:             { label: 'Cert not found',   className: 'bg-gray-100 text-gray-700 border border-gray-200' },
  no_document_attached:  { label: 'No document',      className: 'bg-amber-50 text-amber-700 border border-amber-200' },
  unrecognized_storage:  { label: 'Bad storage',      className: 'bg-rose-50 text-rose-700 border border-rose-200' },
};

const CERT_TYPES = [
  { value: '__all__', label: 'All certification types' },
  { value: 'alcohol_service',  label: 'Alcohol Service Certification' },
  { value: 'food_handler',     label: 'Food Handler Permit' },
  { value: 'drivers_license',  label: "Driver's License" },
  { value: 'security_license', label: 'Security Guard License' },
  { value: 'cpr_first_aid',    label: 'CPR / First Aid' },
  { value: 'osha_10',          label: 'OSHA 10-Hour' },
];

const SUCCESS_OPTIONS = [
  { value: '__all__', label: 'All outcomes' },
  { value: 'true',  label: 'Successes only' },
  { value: 'false', label: 'Failures only' },
];

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) { return iso; }
};

const truncate = (s, n) => (s && s.length > n ? `${s.slice(0, n)}…` : (s || '—'));

const AdminCertAccessLog = () => {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ successes: 0, failures: 0, unique_viewers: 0, unique_talents: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [filterCertType, setFilterCertType] = useState('__all__');
  const [filterSuccess, setFilterSuccess] = useState('__all__');
  const [filterTalentId, setFilterTalentId] = useState('');
  const [filterViewerId, setFilterViewerId] = useState('');
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');

  const [page, setPage] = useState(0);
  const limit = 25;

  const queryParams = useMemo(() => {
    const p = { limit, offset: page * limit };
    if (filterCertType !== '__all__') p.certification_type = filterCertType;
    if (filterSuccess !== '__all__') p.success = filterSuccess;
    if (filterTalentId.trim()) p.talent_user_id = filterTalentId.trim();
    if (filterViewerId.trim()) p.viewer_user_id = filterViewerId.trim();
    if (filterStart) p.start_date = filterStart;
    if (filterEnd) p.end_date = filterEnd;
    return p;
  }, [filterCertType, filterSuccess, filterTalentId, filterViewerId, filterStart, filterEnd, page]);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/certifications/admin/access-log', { params: queryParams });
      const body = res?.data || {};
      setEvents(Array.isArray(body.events) ? body.events : []);
      setTotal(Number(body.total || 0));
      setSummary(body.summary || { successes: 0, failures: 0, unique_viewers: 0, unique_talents: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load access log');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [queryParams]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => { setRefreshing(true); load(); };
  const clearFilters = () => {
    setFilterCertType('__all__');
    setFilterSuccess('__all__');
    setFilterTalentId('');
    setFilterViewerId('');
    setFilterStart('');
    setFilterEnd('');
    setPage(0);
  };

  const exportCsv = () => {
    if (!events.length) { toast.info('Nothing to export on this page'); return; }
    const header = [
      'accessed_at', 'success', 'failure_reason', 'viewer_name', 'viewer_role',
      'viewer_email', 'talent_name', 'talent_email', 'certification_type_name',
      'document_backend', 'ip_address', 'user_agent',
    ];
    const rows = events.map((e) => header.map((h) => {
      const v = e[h];
      const s = v === null || v === undefined ? '' : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    }).join(','));
    const csv = [header.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cert-access-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6 sm:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <Link to="/admin/home" className="text-gray-500 hover:text-gray-900">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-6 h-6 text-indigo-600" />
              Compliance Audit · Certification Access Log
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={exportCsv} data-testid="cert-audit-export-csv-btn">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={refresh}
              disabled={refreshing}
              data-testid="cert-audit-refresh-btn"
            >
              {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              <span className="ml-1">Refresh</span>
            </Button>
          </div>
        </div>

        <p className="text-sm text-gray-500 mb-4 max-w-3xl">
          Every fetch through the certification document proxy is logged here — successes and
          failures alike. Use this feed for SOC2 / state alcohol-board reviews and to spot
          unauthorized access patterns. Failed attempts surface why (unauthorized, not found, etc.).
        </p>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <Card data-testid="cert-audit-summary-success">
            <CardContent className="p-4">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Successes</div>
              <div className="text-2xl font-bold text-emerald-700 mt-1 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5" /> {summary.successes}
              </div>
            </CardContent>
          </Card>
          <Card data-testid="cert-audit-summary-failures">
            <CardContent className="p-4">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Failures</div>
              <div className="text-2xl font-bold text-rose-700 mt-1 flex items-center gap-2">
                <ShieldAlert className="w-5 h-5" /> {summary.failures}
              </div>
            </CardContent>
          </Card>
          <Card data-testid="cert-audit-summary-viewers">
            <CardContent className="p-4">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Unique viewers</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">{summary.unique_viewers}</div>
            </CardContent>
          </Card>
          <Card data-testid="cert-audit-summary-talents">
            <CardContent className="p-4">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Unique talents</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">{summary.unique_talents}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-5">
          <CardHeader className="py-3 px-5">
            <CardTitle className="text-sm flex items-center gap-2">
              <Filter className="w-4 h-4 text-indigo-600" /> Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wide text-gray-500">Cert type</label>
                <Select value={filterCertType} onValueChange={(v) => { setFilterCertType(v); setPage(0); }}>
                  <SelectTrigger data-testid="cert-audit-filter-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CERT_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-gray-500">Outcome</label>
                <Select value={filterSuccess} onValueChange={(v) => { setFilterSuccess(v); setPage(0); }}>
                  <SelectTrigger data-testid="cert-audit-filter-outcome"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SUCCESS_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-gray-500">Talent (UUID)</label>
                <Input
                  value={filterTalentId}
                  onChange={(e) => { setFilterTalentId(e.target.value); setPage(0); }}
                  placeholder="paste talent id"
                  data-testid="cert-audit-filter-talent"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-gray-500">Viewer (UUID)</label>
                <Input
                  value={filterViewerId}
                  onChange={(e) => { setFilterViewerId(e.target.value); setPage(0); }}
                  placeholder="paste viewer id"
                  data-testid="cert-audit-filter-viewer"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-gray-500">Start date</label>
                <Input
                  type="date"
                  value={filterStart}
                  onChange={(e) => { setFilterStart(e.target.value); setPage(0); }}
                  data-testid="cert-audit-filter-start"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-gray-500">End date</label>
                <Input
                  type="date"
                  value={filterEnd}
                  onChange={(e) => { setFilterEnd(e.target.value); setPage(0); }}
                  data-testid="cert-audit-filter-end"
                />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={clearFilters} data-testid="cert-audit-clear-filters-btn">
                Clear filters
              </Button>
              <span className="text-xs text-gray-500">{total} matching event{total === 1 ? '' : 's'}</span>
            </div>
          </CardContent>
        </Card>

        {/* Events table */}
        <Card data-testid="cert-audit-table-card">
          <CardContent className="p-0">
            {loading ? (
              <div className="py-16 flex items-center justify-center text-gray-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
              </div>
            ) : events.length === 0 ? (
              <div className="py-16 text-center text-gray-500 text-sm" data-testid="cert-audit-empty">
                No access events match the current filters.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b border-gray-200 text-gray-600">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold">When</th>
                      <th className="text-left px-4 py-2 font-semibold">Viewer</th>
                      <th className="text-left px-4 py-2 font-semibold">Talent</th>
                      <th className="text-left px-4 py-2 font-semibold">Cert</th>
                      <th className="text-left px-4 py-2 font-semibold">Outcome</th>
                      <th className="text-left px-4 py-2 font-semibold">Backend</th>
                      <th className="text-left px-4 py-2 font-semibold">IP</th>
                      <th className="text-left px-4 py-2 font-semibold">UA</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {events.map((e) => {
                      const failMeta = e.failure_reason
                        ? FAILURE_REASON_META[e.failure_reason] || {
                            label: e.failure_reason,
                            className: 'bg-rose-50 text-rose-700 border border-rose-200',
                          }
                        : null;
                      return (
                        <tr key={e.id} data-testid={`cert-audit-row-${e.id}`} className="hover:bg-gray-50">
                          <td className="px-4 py-2 whitespace-nowrap text-gray-800">{formatDateTime(e.accessed_at)}</td>
                          <td className="px-4 py-2">
                            <div className="font-medium text-gray-900">{e.viewer_name || 'Anonymous'}</div>
                            <div className="text-[10px] text-gray-500 uppercase tracking-wide">{e.viewer_role || '—'}</div>
                          </td>
                          <td className="px-4 py-2">
                            {e.talent_user_id ? (
                              <Link
                                to={`/admin/talent/${e.talent_user_id}`}
                                className="font-medium text-cyan-700 hover:underline"
                              >
                                {e.talent_name || '—'}
                              </Link>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-gray-800">{e.certification_type_name || '—'}</td>
                          <td className="px-4 py-2">
                            {e.success ? (
                              <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" /> Success
                              </Badge>
                            ) : (
                              <Badge className={`${failMeta?.className || 'bg-rose-50 text-rose-700 border border-rose-200'} inline-flex items-center gap-1`}>
                                <XCircle className="w-3 h-3" /> {failMeta?.label || 'Failed'}
                              </Badge>
                            )}
                          </td>
                          <td className="px-4 py-2 text-gray-600">{e.document_backend || '—'}</td>
                          <td className="px-4 py-2 font-mono text-[11px] text-gray-700">{e.ip_address || '—'}</td>
                          <td className="px-4 py-2 text-gray-600" title={e.user_agent || ''}>
                            {truncate(e.user_agent, 28)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {total > limit && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                <div className="text-xs text-gray-500">
                  Showing {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    data-testid="cert-audit-prev-page-btn"
                  >
                    <ChevronLeft className="w-3 h-3" />
                  </Button>
                  <span className="text-xs text-gray-700 px-2">{page + 1} / {totalPages}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page + 1 >= totalPages}
                    data-testid="cert-audit-next-page-btn"
                  >
                    <ChevronRight className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminCertAccessLog;
